import { getAccessToken } from "AccessToken";
import { hotelxSocketUrl } from "client";
import React, { createContext, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';

export const SocketContext = createContext(null); // Use null as the initial value

const readStorageItem = key => {
  const value = localStorage.getItem(key);

  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

const getStoredSession = () => {
  const hotel = readStorageItem('Hotel');
  const user = readStorageItem('LoggedInUser');

  if (!hotel?.ID || !user?.ID || !getAccessToken()) {
    return null;
  }

  return {
    HotelID: hotel.ID,
    UserID: user.ID,
  }
}

export const SocketContextProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const urlInfo =  window.location.pathname
  const sessionRef = useRef(null);
  const syncedSessionRef = useRef(null);

  const emitSocketSession = (targetSocket, session) => {
    if (!targetSocket || !session?.HotelID || !session?.UserID) {
      return;
    }

    targetSocket.emit('data', {
      Status: 'LOGIN',
      HotelID: session.HotelID,
      UserID: session.UserID,
    });

    targetSocket.emit('joinHotel', session.HotelID);
    syncedSessionRef.current = session;

    const urlPage = window.location.pathname
    const folioPage = urlPage?.includes('/payment/folio')
    const userLock = readStorageItem('LoggedInUser')?.LockUserProcess

    if (folioPage === true && userLock === true) {
      window.history.back();
    }
  }

  const syncSocketSession = session => {
    if (!session?.HotelID || !session?.UserID || !getAccessToken()) {
      return;
    }

    const nextSession = {
      HotelID: session.HotelID,
      UserID: session.UserID,
    }

    sessionRef.current = nextSession;

    if (!socket) {
      return;
    }

    const syncedSession = syncedSessionRef.current;
    const isSameSession = syncedSession?.HotelID === nextSession.HotelID && syncedSession?.UserID === nextSession.UserID;

    if (socket.connected && isSameSession) {
      return;
    }

    if (socket.connected) {
      emitSocketSession(socket, nextSession);
      return;
    }

    socket.connect();
  }

  useEffect(() => {
    const newSocket =  
    io(hotelxSocketUrl,{
      transports: ["websocket"], // force WebSocket only
      withCredentials: true,
      reconnection: true,
      autoConnect: false,
    });
    setSocket(newSocket);

    // Show the popup dialog when the user tries to leave the page
    const onBeforeUnload = () => {
      const currentSession = sessionRef.current || getStoredSession();

      if (!currentSession) {
        return;
      }

      newSocket.emit('data', {
        Status: 'LOGOUT',
        HotelID: currentSession.HotelID,
        UserID: currentSession.UserID,
      });
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    newSocket.on("connect", () => {
      const restoredSession = sessionRef.current || getStoredSession();

      if (restoredSession) {
        sessionRef.current = restoredSession;
        emitSocketSession(newSocket, restoredSession);
      }
    });

    const restoredSession = getStoredSession();

    if (restoredSession) {
      sessionRef.current = restoredSession;
      newSocket.connect();
    }

    newSocket.on('disconnect', () => {
      syncedSessionRef.current = null;
    });

    // Cleanup function
    return () => {
      sessionRef.current = null;
      syncedSessionRef.current = null;
      newSocket.disconnect();
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!socket) {
      return;
    }

    const restoredSession = sessionRef.current || getStoredSession();

    if (restoredSession) {
      syncSocketSession(restoredSession);
    }
  }, [socket])

  useEffect(() => {
    const urlPage =  window.location.pathname
    const folioPage = urlPage?.includes('/payment/folio')
    const userLock = readStorageItem('LoggedInUser')?.LockUserProcess

    if (socket) {
      if (folioPage === false && userLock === true ) {
        socket.emit('ClearUserProcess', {
          HotelID: readStorageItem('Hotel')?.ID,
          UserID: readStorageItem('LoggedInUser')?.ID,
        });
        const loggedInUserInfo = readStorageItem('LoggedInUser') || {}
        loggedInUserInfo.LockUserProcess = false
        localStorage.setItem('LoggedInUser', JSON.stringify(loggedInUserInfo))
        localStorage.removeItem('RoomNo')
      }
    }
  }, [socket, urlInfo])

  const LOGOUT = () => {
    const currentSession = sessionRef.current || getStoredSession();

    if (socket?.connected && currentSession) {
      socket.emit('data', {
        Status: 'LOGOUT',
        HotelID: currentSession.HotelID,
        UserID: currentSession.UserID,
      });
    }

    sessionRef.current = null;
    syncedSessionRef.current = null;

    if (socket) {
      socket.disconnect();
    }
  }

  return (
    <SocketContext.Provider
      value={{
        socket,
        LOGOUT,
        syncSocketSession,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
